import { useState, useCallback, useEffect, useRef } from 'react';
import { exportAllData, resetAllData } from '@/lib/supabaseDb';
import { importBackup, verifyImportBackup, ImportBackupResult } from '@/lib/importService';
import { getExchangeRateSettings, saveExchangeRateSettings } from '@/lib/exchangeRateService';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';

function SettingsSection({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <i className={`${icon} text-primary-600 text-lg`}></i>
        </span>
        <h4 className="font-semibold text-foreground-800 font-heading">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function ExchangeRateSettingsForm() {
  const [settings, setSettings] = useState(getExchangeRateSettings());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings(getExchangeRateSettings());
  }, []);

  const handleToggleAutoRefresh = () => {
    const updated = { ...settings, auto_refresh: !settings.auto_refresh };
    setSettings(updated);
    saveExchangeRateSettings(updated);
    showSaved();
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const updated = { ...settings, refresh_interval: parseInt(e.target.value, 10) };
    setSettings(updated);
    saveExchangeRateSettings(updated);
    showSaved();
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const updated = { ...settings, provider: e.target.value as 'auto' | 'open-er-api' | 'frankfurter' };
    setSettings(updated);
    saveExchangeRateSettings(updated);
    showSaved();
  };

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      {saved && (
        <div className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <i className="ri-check-line"></i>
          <span>Ayarlar kaydedildi</span>
        </div>
      )}

      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm font-medium text-foreground-700">Otomatik Yenileme</p>
          <p className="text-xs text-foreground-400">Belirli aralıklarla kur bilgilerini otomatik günceller</p>
        </div>
        <button
          onClick={handleToggleAutoRefresh}
          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${settings.auto_refresh ? 'bg-emerald-500' : 'bg-background-300'}`}
        >
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${settings.auto_refresh ? 'left-[22px]' : 'left-0.5'}`}></span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yenileme Aralığı</label>
          <select
            value={settings.refresh_interval}
            onChange={handleIntervalChange}
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 cursor-pointer"
          >
            <option value={1}>1 dakika</option>
            <option value={5}>5 dakika</option>
            <option value={15}>15 dakika</option>
            <option value={30}>30 dakika</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-600 mb-1.5">API Sağlayıcı</label>
          <select
            value={settings.provider}
            onChange={handleProviderChange}
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 cursor-pointer"
          >
            <option value="auto">Otomatik (Önerilen)</option>
            <option value="open-er-api">ExchangeRate API</option>
            <option value="frankfurter">Frankfurter</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function ElogoSettings() {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    testedAt: string;
    steps: {
      wsdl: { status?: string; httpStatus?: number; contentType?: string; error?: string; reason?: string; targetNamespace?: string; endpointAddress?: string; operations?: string[]; soapActions?: { name: string; soapAction: string }[]; soapVersion?: string; responseLength?: number; responsePreview?: string; looksLikeHtml?: boolean; parseError?: string; contractName?: string; serviceUrlUsed?: string };
      ping?: { status?: string; error?: string; httpStatus?: number; soapActionUsed?: string; pong?: string };
      login: { status?: string; error?: string; httpStatus?: number; soapActionUsed?: string; wsdlSoapAction?: string; actionDerived?: string; soapVersionUsed?: string; contentTypeUsed?: string; isSoapFault?: boolean; faultCode?: string; faultString?: string; requestPreview?: string };
      logout: { status?: string; error?: string; httpStatus?: number; reason?: string; soapActionUsed?: string };
    };
    credentialDiagnostics?: {
      usernameSecretExists: boolean;
      passwordSecretExists: boolean;
      usernameLength: number;
      passwordLength: number;
      credentialsTrimmed: boolean;
    };
    loginFieldDiagnostics?: {
      appStr: string;
      source: string;
      version: string;
      appStrSource: string;
      sourceType: string;
      versionType: string;
      loginTypeNamespace: string;
      wrapperElement: string;
      parameterElement: string;
      fieldOrder: string[];
      userNameMappedTo: string;
      passWordMappedTo: string;
    };
  } | null>(null);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    const { testElogoConnection } = await import('@/lib/elogoDb');
    const result = await testElogoConnection();

    setTesting(false);

    if (result.data) {
      setTestResult(result.data);
    } else {
      setTestResult({
        success: false,
        message: result.error || 'Bağlantı testi başarısız',
        testedAt: new Date().toISOString(),
        steps: {
          wsdl: { status: 'failed', error: result.error || 'İstek başarısız' },
          login: { status: 'skipped' },
          logout: { status: 'skipped' },
        },
      });
    }
  };

  const stepLabel = (label: string, step: { status?: string; error?: string; reason?: string } | undefined) => {
    if (!step || !step.status) return null;
    const st = step.status;
    const done = st === 'successful';
    const failed = st === 'failed';
    const skipped = st === 'skipped';

    let colorClass = 'text-foreground-400';
    let bgClass = 'bg-background-200';
    let icon = 'ri-more-line';
    let statusText = 'Bekleniyor';

    if (done) {
      colorClass = 'text-emerald-600';
      bgClass = 'bg-emerald-100';
      icon = 'ri-check-line';
      statusText = 'Başarılı';
    } else if (failed) {
      colorClass = 'text-red-500';
      bgClass = 'bg-red-100';
      icon = 'ri-close-line';
      statusText = 'Başarısız';
    } else if (skipped) {
      colorClass = 'text-amber-500';
      bgClass = 'bg-amber-100';
      icon = 'ri-skip-forward-line';
      statusText = 'Atlandı';
    }

    const errorMsg = failed ? (step.error || '') : skipped ? (step.reason || '') : '';

    return (
      <div className={`flex items-center gap-2 text-xs ${colorClass}`}>
        <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${bgClass}`}>
          <i className={`${icon} text-[10px]`}></i>
        </span>
        <span className="font-medium">{label}: {statusText}</span>
        {errorMsg && <span className="text-red-400 truncate max-w-[200px]">— {errorMsg}</span>}
      </div>
    );
  };

  const hasWsdlFailed = testResult?.steps.wsdl?.status === 'failed';
  const hasLoginFailed = testResult?.steps.login?.status === 'failed';
  const hasLoginSkipped = testResult?.steps.login?.status === 'skipped';
  const hasLogoutSkipped = testResult?.steps.logout?.status === 'skipped';

  // Determine overall test status text
  let testStatusLabel = '';
  let testStatusIcon = 'ri-error-warning-line';
  let testStatusBg = 'bg-amber-100';
  let testStatusColor = 'text-amber-600';
  let cardBg = 'bg-amber-50';
  let cardBorder = 'border-amber-200';

  if (testResult) {
    const loginOk = testResult.steps.login.status === 'successful';
    const logoutOk = testResult.steps.logout.status === 'successful';
    const wsdlOk = testResult.steps.wsdl.status === 'successful';

    if (wsdlOk && loginOk && logoutOk) {
      testStatusLabel = 'Tüm Adımlar Başarılı';
      testStatusIcon = 'ri-check-line';
      testStatusBg = 'bg-emerald-100';
      testStatusColor = 'text-emerald-600';
      cardBg = 'bg-emerald-50';
      cardBorder = 'border-emerald-200';
    } else if (loginOk && !wsdlOk) {
      testStatusLabel = 'Bağlantı Başarılı (WSDL Meta Verisi Okunamadı)';
      testStatusIcon = 'ri-check-line';
      testStatusBg = 'bg-emerald-100';
      testStatusColor = 'text-emerald-600';
      cardBg = 'bg-emerald-50';
      cardBorder = 'border-emerald-200';
    } else if (hasLoginFailed || hasLoginSkipped) {
      testStatusLabel = 'Bağlantı Testi Başarısız';
      testStatusIcon = 'ri-close-circle-line';
      testStatusBg = 'bg-red-100';
      testStatusColor = 'text-red-600';
      cardBg = 'bg-red-50';
      cardBorder = 'border-red-200';
    } else {
      testStatusLabel = 'Test Tamamlandı (Bazı Adımlar Başarısız)';
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-foreground-500">e-Fatura ve e-Arşiv entegrasyonu test ortamında yapılandırılmıştır. Production bağlantısı şimdilik aktif değildir.</p>

      {/* Durum Kartı */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-background-100 rounded-xl p-4 flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${testResult?.success ? 'bg-emerald-500' : testResult && !testResult.success && testResult.steps?.login?.status === 'successful' ? 'bg-emerald-500' : testResult ? 'bg-red-500' : 'bg-background-300'}`}></span>
          <div>
            <p className="text-xs text-foreground-400">Ortam</p>
            <p className="text-sm font-semibold text-foreground-700">Test (pb-demo.elogo.com.tr)</p>
          </div>
        </div>
        <div className="bg-background-100 rounded-xl p-4">
          <p className="text-xs text-foreground-400">Gönderim Modu</p>
          <p className="text-sm font-semibold text-foreground-700 flex items-center gap-1.5">
            <i className="ri-draft-line text-amber-500"></i>
            Taslak Oluştur (Güvenli Mod)
          </p>
          <p className="text-[11px] text-foreground-400 mt-0.5">Faturalar önce taslak oluşturulur, onay sonrası gönderilir</p>
        </div>
      </div>

      {/* Test Butonu */}
      <button
        onClick={handleTestConnection}
        disabled={testing}
        className="btn-outline flex items-center gap-2 px-5 py-2.5 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {testing ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            <span>Test Ediliyor (WSDL + Login + Logout)...</span>
          </>
        ) : (
          <>
            <i className="ri-plug-line"></i>
            <span>Test Bağlantısı (WSDL + Login + Logout)</span>
          </>
        )}
      </button>

      {/* Adım Adım Sonuçlar */}
      {testResult && (
        <div className={`p-4 rounded-xl border ${cardBg} ${cardBorder}`}>
          <div className="flex items-start gap-3 mb-4">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${testStatusBg}`}>
              <i className={`${testStatusIcon} ${testStatusColor}`}></i>
            </span>
            <div>
              <p className={`text-sm font-semibold ${testStatusColor}`}>
                {testStatusLabel}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">{testResult.message}</p>
              <p className="text-[10px] text-foreground-300 mt-1">
                Son test: {new Date(testResult.testedAt).toLocaleString('tr-TR')}
              </p>
            </div>
          </div>

          {/* Adım listesi */}
          <div className="space-y-2 pl-11">
            {/* LoginType Field Diagnostics */}
            {testResult.loginFieldDiagnostics && (
              <details className="mb-1">
                <summary className="text-[10px] text-foreground-400 cursor-pointer font-medium">LoginType Yapı Diagnostiği</summary>
                <div className="mt-1.5 p-2.5 bg-background-100 rounded-lg space-y-1">
                  <div className="text-[10px] text-foreground-500">Namespace: <span className="text-foreground-700 font-mono break-all">{testResult.loginFieldDiagnostics.loginTypeNamespace}</span></div>
                  <div className="text-[10px] text-foreground-500">Wrapper: <span className="text-foreground-700 font-mono">{testResult.loginFieldDiagnostics.wrapperElement}</span></div>
                  <div className="text-[10px] text-foreground-500">Parametre: <span className="text-foreground-700 font-mono">{testResult.loginFieldDiagnostics.parameterElement}</span></div>
                  <div className="text-[10px] text-foreground-500">Alan sırası: <span className="text-foreground-700 font-mono">{testResult.loginFieldDiagnostics.fieldOrder.join(', ')}</span></div>
                  <div className="text-[10px] text-foreground-500">appStr: <span className="text-foreground-700">{testResult.loginFieldDiagnostics.appStr}</span> <span className="text-foreground-300">({testResult.loginFieldDiagnostics.appStrSource})</span></div>
                  <div className="text-[10px] text-foreground-500">source: <span className="text-foreground-700">{testResult.loginFieldDiagnostics.source}</span> <span className="text-foreground-300">({testResult.loginFieldDiagnostics.sourceType})</span></div>
                  <div className="text-[10px] text-foreground-500">version: <span className="text-foreground-700">{testResult.loginFieldDiagnostics.version}</span></div>
                  <div className="text-[10px] text-foreground-500">userName &rarr; <span className="text-foreground-700">{testResult.loginFieldDiagnostics.userNameMappedTo}</span></div>
                  <div className="text-[10px] text-foreground-500">passWord &rarr; <span className="text-foreground-700">{testResult.loginFieldDiagnostics.passWordMappedTo}</span></div>
                </div>
              </details>
            )}
            {testResult.credentialDiagnostics && (
              <details className="mb-1">
                <summary className="text-[10px] text-foreground-400 cursor-pointer font-medium">Kimlik Bilgisi Diagnostiği</summary>
                <div className="mt-1.5 p-2.5 bg-background-100 rounded-lg space-y-1">
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${testResult.credentialDiagnostics.usernameSecretExists ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                    <span className="text-foreground-500">Kullanıcı Adı Secret:</span>
                    <span className={testResult.credentialDiagnostics.usernameSecretExists ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                      {testResult.credentialDiagnostics.usernameSecretExists ? 'Mevcut' : 'Eksik!'}
                    </span>
                    {testResult.credentialDiagnostics.usernameSecretExists && (
                      <span className="text-foreground-300">({testResult.credentialDiagnostics.usernameLength} karakter)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${testResult.credentialDiagnostics.passwordSecretExists ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                    <span className="text-foreground-500">Şifre Secret:</span>
                    <span className={testResult.credentialDiagnostics.passwordSecretExists ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                      {testResult.credentialDiagnostics.passwordSecretExists ? 'Mevcut' : 'Eksik!'}
                    </span>
                    {testResult.credentialDiagnostics.passwordSecretExists && (
                      <span className="text-foreground-300">({testResult.credentialDiagnostics.passwordLength} karakter)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${testResult.credentialDiagnostics.credentialsTrimmed ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                    <span className="text-foreground-500">Baş/Son Boşluk Temizlendi:</span>
                    <span className={testResult.credentialDiagnostics.credentialsTrimmed ? 'text-amber-600 font-medium' : 'text-foreground-400'}>
                      {testResult.credentialDiagnostics.credentialsTrimmed ? 'Evet (boşluk vardı, temizlendi)' : 'Gerekmedi (boşluk yoktu)'}
                    </span>
                  </div>
                </div>
              </details>
            )}
            {stepLabel('WSDL Erişimi', testResult.steps.wsdl as any)}
            {testResult.steps.wsdl?.status === 'failed' && (
              <div className="pl-6 space-y-0.5">
                {testResult.steps.wsdl.targetNamespace && (
                  <p className="text-[10px] text-foreground-400">Namespace: {testResult.steps.wsdl.targetNamespace}</p>
                )}
                {testResult.steps.wsdl.contractName && (
                  <p className="text-[10px] text-foreground-400">Kontrat: {testResult.steps.wsdl.contractName}</p>
                )}
                {testResult.steps.wsdl.endpointAddress && (
                  <p className="text-[10px] text-foreground-400 truncate">Endpoint: {testResult.steps.wsdl.endpointAddress}</p>
                )}
                {testResult.steps.wsdl.operations && testResult.steps.wsdl.operations.length > 0 && (
                  <p className="text-[10px] text-foreground-400">Operations: {testResult.steps.wsdl.operations.join(', ')}</p>
                )}
                {testResult.steps.wsdl.httpStatus !== undefined && (
                  <p className="text-[10px] text-foreground-400">HTTP Status: {testResult.steps.wsdl.httpStatus}</p>
                )}
                {testResult.steps.wsdl.contentType && (
                  <p className="text-[10px] text-foreground-400">Content-Type: {testResult.steps.wsdl.contentType}</p>
                )}
                {testResult.steps.wsdl.responseLength !== undefined && (
                  <p className="text-[10px] text-foreground-400">Response: {testResult.steps.wsdl.responseLength} byte</p>
                )}
                {testResult.steps.wsdl.looksLikeHtml && (
                  <p className="text-[10px] text-red-400">UYARI: WSDL yanıtı XML değil HTML!</p>
                )}
                {testResult.steps.wsdl.parseError && (
                  <p className="text-[10px] text-red-400">Parse Hatası: {testResult.steps.wsdl.parseError}</p>
                )}
                {testResult.steps.wsdl.responsePreview && (
                  <details className="mt-1">
                    <summary className="text-[10px] text-foreground-400 cursor-pointer">Yanıt önizlemesi (ilk 300 karakter)</summary>
                    <pre className="text-[9px] text-foreground-400 bg-background-100 p-1.5 rounded mt-1 max-h-[100px] overflow-auto whitespace-pre-wrap break-all">{testResult.steps.wsdl.responsePreview}</pre>
                  </details>
                )}
              </div>
            )}
            {testResult.steps.wsdl?.status === 'successful' && (
              <div className="pl-6 space-y-0.5">
                {testResult.steps.wsdl.targetNamespace && (
                  <p className="text-[10px] text-foreground-400">Namespace: {testResult.steps.wsdl.targetNamespace}</p>
                )}
                {testResult.steps.wsdl.contractName && (
                  <p className="text-[10px] text-foreground-400">Kontrat: {testResult.steps.wsdl.contractName}</p>
                )}
                {testResult.steps.wsdl.endpointAddress && (
                  <p className="text-[10px] text-foreground-400 truncate">Endpoint: {testResult.steps.wsdl.endpointAddress}</p>
                )}
                {testResult.steps.wsdl.soapVersion && (
                  <p className="text-[10px] text-foreground-400">SOAP: v{testResult.steps.wsdl.soapVersion}</p>
                )}
                {testResult.steps.wsdl.operations && testResult.steps.wsdl.operations.length > 0 && (
                  <p className="text-[10px] text-foreground-400">Operations: {testResult.steps.wsdl.operations.join(', ')}</p>
                )}
                {testResult.steps.wsdl.soapActions && testResult.steps.wsdl.soapActions.length > 0 && (
                  <details className="mt-0.5">
                    <summary className="text-[10px] text-foreground-400 cursor-pointer">SOAP Action'lar</summary>
                    <div className="mt-1 pl-2 space-y-0.5">
                      {testResult.steps.wsdl.soapActions.map((sa, i) => (
                        <p key={i} className="text-[9px] text-foreground-400">
                          <span className="font-medium">{sa.name}</span>: <span className="break-all">{sa.soapAction || '(boş)'}</span>
                        </p>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
            {/* Ping */}
            {testResult.steps.ping && stepLabel('Ping (Endpoint Testi)', testResult.steps.ping as any)}
            {testResult.steps.ping?.pong && (
              <p className="text-[10px] text-emerald-600 pl-6">Pong yanıtı: {testResult.steps.ping.pong}</p>
            )}
            {testResult.steps.ping?.status === 'failed' && testResult.steps.ping.error && (
              <p className="text-[10px] text-red-400 pl-6 break-all">{testResult.steps.ping.error}</p>
            )}

            {stepLabel('Login', testResult.steps.login as any)}
            {testResult.steps.login?.httpStatus !== undefined && (
              <p className="text-[10px] text-foreground-400 pl-6">HTTP Status: {testResult.steps.login.httpStatus}</p>
            )}
            {testResult.steps.login?.soapVersionUsed && (
              <p className="text-[10px] text-foreground-400 pl-6">SOAP Sürümü: v{testResult.steps.login.soapVersionUsed}</p>
            )}
            {testResult.steps.login?.soapActionUsed && (
              <details className="pl-6 mt-0.5">
                <summary className="text-[10px] text-foreground-400 cursor-pointer">SOAPAction Detayı</summary>
                <div className="mt-1 pl-2 space-y-0.5">
                  <p className="text-[9px] text-foreground-400 break-all">Kullanılan: {testResult.steps.login.soapActionUsed}</p>
                  {testResult.steps.login.actionDerived && (
                    <p className="text-[9px] text-foreground-400">Kaynak: {testResult.steps.login.actionDerived === 'WSDL exact' ? 'WSDL\'den birebir alındı' : 'Namespace + kontrat adı ile türetildi (fallback)'}</p>
                  )}
                  {testResult.steps.login.wsdlSoapAction && testResult.steps.login.wsdlSoapAction !== '(WSDL\'den alınamadı)' && (
                    <p className="text-[9px] text-foreground-400 break-all">WSDL'deki: {testResult.steps.login.wsdlSoapAction}</p>
                  )}
                </div>
              </details>
            )}
            {testResult.steps.login?.contentTypeUsed && (
              <p className="text-[10px] text-foreground-400 pl-6">Content-Type: {testResult.steps.login.contentTypeUsed}</p>
            )}
            {testResult.steps.login?.requestPreview && (
              <details className="pl-6 mt-0.5">
                <summary className="text-[10px] text-foreground-400 cursor-pointer">Login Request Önizlemesi (Maskelenmiş)</summary>
                <pre className="text-[9px] text-foreground-500 bg-background-100 p-1.5 rounded mt-1 max-h-[180px] overflow-auto whitespace-pre-wrap font-mono">{testResult.steps.login.requestPreview}</pre>
              </details>
            )}
            {testResult.steps.login?.status === 'failed' && (
              <>
                {testResult.steps.login.isSoapFault && (
                  <div className="pl-6 space-y-0.5">
                    {testResult.steps.login.faultCode && (
                      <p className="text-[10px] text-red-400">Fault Code: {testResult.steps.login.faultCode}</p>
                    )}
                    {testResult.steps.login.faultString && (
                      <p className="text-[10px] text-red-400 break-all">Fault String: {testResult.steps.login.faultString}</p>
                    )}
                  </div>
                )}
                {testResult.steps.login.error && (
                  <details className="pl-6 mt-0.5">
                    <summary className="text-[10px] text-red-400 cursor-pointer">Tam Hata Detayı</summary>
                    <pre className="text-[9px] text-red-400 bg-red-50 p-1.5 rounded mt-1 max-h-[150px] overflow-auto whitespace-pre-wrap break-all">{testResult.steps.login.error}</pre>
                  </details>
                )}
              </>
            )}
            {stepLabel('Logout', testResult.steps.logout as any)}
            {testResult.steps.logout?.httpStatus !== undefined && (
              <p className="text-[10px] text-foreground-400 pl-6">HTTP Status: {testResult.steps.logout.httpStatus}</p>
            )}
          </div>
        </div>
      )}

      <div className="border-t border-background-100 pt-4 mt-2">
        <p className="text-xs text-foreground-400">
          <i className="ri-information-line mr-1"></i>
          Bu entegrasyon yalnızca eLogo TEST ortamında çalışır. Gerçek fatura gönderimi için production yapılandırması henüz aktif değildir.
        </p>
      </div>
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  sales: 'Satış Personeli',
  accounting: 'Muhasebe',
  warehouse: 'Depo',
  delivery: 'Teslimat',
};

export default function SettingsPage() {
  const { profile, user, role, roleLabel, updateProfile, updateEmail, updatePassword, refreshProfile } = useAuth();

  // Toast
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Profil form
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  // Şifre form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Avatar
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Modals
  const [showResetModal, setShowResetModal] = useState(false);
  const [showClearDemoModal, setShowClearDemoModal] = useState(false);

  // İşletme Bilgileri
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [bizName, setBizName] = useState('');
  const [bizType, setBizType] = useState('');
  const [bizTaxNumber, setBizTaxNumber] = useState('');
  const [bizTaxOffice, setBizTaxOffice] = useState('');
  const [bizAuthorizedName, setBizAuthorizedName] = useState('');
  const [bizAuthorizedPhone, setBizAuthorizedPhone] = useState('');
  const [bizAuthorizedEmail, setBizAuthorizedEmail] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizAddress, setBizAddress] = useState('');
  const [bizCity, setBizCity] = useState('');
  const [bizDistrict, setBizDistrict] = useState('');
  const [bizNeighborhood, setBizNeighborhood] = useState('');
  const [bizCurrency, setBizCurrency] = useState('TRY');
  const [bizStatus, setBizStatus] = useState('Aktif');
  const [bizLogoUrl, setBizLogoUrl] = useState('');
  const [bizLogoUploading, setBizLogoUploading] = useState(false);
  const [bizLogoRemoving, setBizLogoRemoving] = useState(false);
  const [bizLoading, setBizLoading] = useState(true);
  const [bizSaving, setBizSaving] = useState(false);
  const bizLogoFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!supabase) { setBizLoading(false); return; }
    supabase
      .from('businesses')
      .select('*')
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('loadBusiness error:', error);
        } else if (data) {
          setBusinessId(data.id);
          setBizName(data.name || '');
          setBizType(data.type || '');
          setBizTaxNumber(data.tax_number || '');
          setBizTaxOffice(data.tax_office || '');
          setBizAuthorizedName(data.authorized_person_name || '');
          setBizAuthorizedPhone(data.authorized_person_phone || '');
          setBizAuthorizedEmail(data.authorized_person_email || '');
          setBizPhone(data.phone || '');
          setBizEmail(data.email || '');
          setBizAddress(data.address || '');
          setBizCity(data.city || '');
          setBizDistrict(data.district || '');
          setBizNeighborhood(data.neighborhood || '');
          setBizCurrency(data.currency || 'TRY');
          setBizStatus(data.status || 'Aktif');
          setBizLogoUrl(data.logo_url || '');
        }
        setBizLoading(false);
      });
  }, []);

  const handleBusinessLogoUpload = async () => {
    if (!supabase || !user || !businessId) return;
    const file = bizLogoFileRef.current?.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast('Logo 2MB\'dan küçük olmalıdır.', 'error');
      return;
    }
    if (!file.type.startsWith('image/')) {
      showToast('Lütfen bir resim dosyası seçin.', 'error');
      return;
    }

    setBizLogoUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const filePath = `${user.id}/business-logo-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        showToast('Logo yüklenemedi: ' + uploadError.message, 'error');
        return;
      }

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const logoUrl = urlData?.publicUrl;
      if (!logoUrl) {
        showToast('Logo yüklenemedi.', 'error');
        return;
      }

      const { error: updateError } = await supabase
        .from('businesses')
        .update({ logo_url: logoUrl, updated_at: new Date().toISOString() })
        .eq('id', businessId);

      if (updateError) {
        showToast('Logo kaydedilemedi: ' + updateError.message, 'error');
        return;
      }

      setBizLogoUrl(logoUrl);
      await refreshProfile();
      showToast('İşletme logosu güncellendi.', 'success');
    } catch {
      showToast('Logo yüklenemedi.', 'error');
    } finally {
      setBizLogoUploading(false);
      if (bizLogoFileRef.current) bizLogoFileRef.current.value = '';
    }
  };

  const handleBusinessLogoRemove = async () => {
    if (!supabase || !businessId || !bizLogoUrl) return;
    setBizLogoRemoving(true);
    try {
      const { error: updateError } = await supabase
        .from('businesses')
        .update({ logo_url: null, updated_at: new Date().toISOString() })
        .eq('id', businessId);

      if (updateError) {
        showToast('Logo kaldırılamadı: ' + updateError.message, 'error');
        return;
      }

      // Depolamadaki dosyayı da silmeyi dene — başarısız olsa da işlemi bozma,
      // asıl olan businesses.logo_url'in null olması (Sidebar/Header buna göre
      // otomatik işletme adı + ikon fallback'ine döner).
      try {
        const marker = '/avatars/';
        const idx = bizLogoUrl.indexOf(marker);
        if (idx !== -1) {
          const storagePath = bizLogoUrl.slice(idx + marker.length);
          if (storagePath) await supabase.storage.from('avatars').remove([storagePath]);
        }
      } catch {
        // depolama temizliği kritik değil, sessizce yut
      }

      setBizLogoUrl('');
      await refreshProfile();
      showToast('İşletme logosu kaldırıldı.', 'success');
    } catch {
      showToast('Logo kaldırılamadı.', 'error');
    } finally {
      setBizLogoRemoving(false);
      if (bizLogoFileRef.current) bizLogoFileRef.current.value = '';
    }
  };

  const handleBusinessSave = async () => {
    if (!supabase || !businessId) return;
    if (!bizName.trim()) {
      showToast('İşletme adı zorunludur.', 'error');
      return;
    }
    setBizSaving(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: bizName.trim(),
          type: bizType.trim() || null,
          tax_number: bizTaxNumber.trim() || null,
          tax_office: bizTaxOffice.trim() || null,
          authorized_person_name: bizAuthorizedName.trim() || null,
          authorized_person_phone: bizAuthorizedPhone.trim() || null,
          authorized_person_email: bizAuthorizedEmail.trim() || null,
          phone: bizPhone.trim() || null,
          email: bizEmail.trim() || null,
          address: bizAddress.trim() || null,
          city: bizCity.trim() || null,
          district: bizDistrict.trim() || null,
          neighborhood: bizNeighborhood.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', businessId);

      if (error) {
        showToast('İşletme bilgileri güncellenemedi: ' + error.message, 'error');
      } else {
        showToast('İşletme bilgileri güncellendi.', 'success');
      }
    } catch {
      showToast('İşletme bilgileri güncellenemedi.', 'error');
    } finally {
      setBizSaving(false);
    }
  };

  const { triggerRefresh } = useDataRefresh();

  // Profil verisi değişince form alanlarını güncelle
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setPhone(profile.phone || '');
    }
    if (user) {
      setEmailInput(user.email || '');
    }
  }, [profile, user]);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setToastMsg(null), 3500);
  }, []);

  // Profil Kaydet
  const handleProfileSave = async () => {
    if (!user) return;
    setProfileSaving(true);
    try {
      const { success, error } = await updateProfile({
        full_name: fullName.trim(),
        phone: phone.trim(),
      });
      if (success) {
        showToast('Profil bilgileriniz güncellendi.', 'success');

        // E-posta değişmişse Auth e-postasını da güncelle
        if (emailInput.trim() !== user.email) {
          setEmailSaving(true);
          const emailResult = await updateEmail(emailInput.trim());
          if (emailResult.success) {
            showToast('E-posta değişikliği için doğrulama bağlantısı gönderildi. Yeni e-posta adresinizi kontrol edin.', 'success');
          } else {
            showToast(emailResult.error || 'E-posta güncellenemedi.', 'error');
          }
          setEmailSaving(false);
        }
      } else {
        showToast(error || 'Profil bilgileri güncellenemedi.', 'error');
      }
    } catch {
      showToast('Profil bilgileri güncellenemedi.', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // Şifre Güncelle
  const handlePasswordUpdate = async () => {
    setPasswordError('');
    // Validasyon
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Tüm şifre alanları zorunludur.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Yeni şifre en az 8 karakter olmalıdır.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifre ve tekrarı eşleşmiyor.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('Yeni şifre mevcut şifre ile aynı olamaz.');
      return;
    }

    setPasswordSaving(true);
    try {
      const { success, error } = await updatePassword(currentPassword, newPassword);
      if (success) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        showToast('Şifreniz başarıyla güncellendi.', 'success');
      } else {
        setPasswordError(error || 'Şifre güncellenemedi.');
      }
    } catch {
      setPasswordError('Şifre güncellenemedi.');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Avatar yükleme
  const handleAvatarUpload = async () => {
    if (!supabase || !user) return;
    const file = avatarFileRef.current?.files?.[0];
    if (!file) return;

    // Dosya boyutu sınırı: 2MB
    if (file.size > 2 * 1024 * 1024) {
      showToast('Fotoğraf 2MB\'dan küçük olmalıdır.', 'error');
      return;
    }

    // Dosya türü kontrolü
    if (!file.type.startsWith('image/')) {
      showToast('Lütfen bir resim dosyası seçin.', 'error');
      return;
    }

    setAvatarUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const filePath = `${user.id}/avatar-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        showToast('Fotoğraf yüklenemedi: ' + uploadError.message, 'error');
        setAvatarUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const avatarUrl = urlData?.publicUrl;

      if (avatarUrl) {
        await updateProfile({ avatar_url: avatarUrl });
        showToast('Profil fotoğrafı güncellendi.', 'success');
      }
    } catch {
      showToast('Fotoğraf yüklenemedi.', 'error');
    } finally {
      setAvatarUploading(false);
      if (avatarFileRef.current) avatarFileRef.current.value = '';
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const result = await exportAllData();
      if (result.error) { showToast('Dışa aktarma başarısız oldu', 'error'); return; }
      const json = result.data || '';
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `polatlar-yedek-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Veriler başarıyla dışa aktarıldı');
    } catch {
      showToast('Dışa aktarma başarısız oldu', 'error');
    }
  };

  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportBackupResult | null>(null);
  const [importVerifyChecks, setImportVerifyChecks] = useState<{ label: string; passed: boolean; detail: string }[]>([]);
  const [importError, setImportError] = useState<string | null>(null);

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImporting(true);
      setImportResult(null);
      setImportVerifyChecks([]);
      setImportError(null);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target?.result as string;
        try {
          const data = JSON.parse(text);
          const result = await importBackup(data);

          if (!result.success) {
            // Validasyon hatası
            const firstErr = result.tables.find(t => t.errors.length > 0);
            if (firstErr) {
              setImportError(`Validasyon hatası (${firstErr.table}): ${firstErr.errors[0]}`);
            } else {
              setImportError('İçe aktarma başarısız oldu. JSON yapısını kontrol edin.');
            }
            setImporting(false);
            return;
          }

          // İçe aktarma sonrası doğrulama
          const verify = await verifyImportBackup(result.customerIdMap, result.noteIdMap);
          setImportVerifyChecks(verify.checks);
          setImportResult(result);
          setImporting(false);
          showToast('Yedek geri yükleme tamamlandı. Detaylar aşağıda.', 'success');
        } catch (parseErr) {
          setImportError('Geçersiz JSON dosyası. Dosya bozuk veya format hatalı.');
          setImporting(false);
        }
      };
      reader.onerror = () => {
        setImportError('Dosya okunamadı.');
        setImporting(false);
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleClearDemoData = async () => {
    const { error } = await resetAllData();
    if (error) {
      setShowClearDemoModal(false);
      showToast('Veri temizleme başarısız: ' + error, 'error');
      return;
    }
    triggerRefresh();
    setShowClearDemoModal(false);
    showToast('Tüm demo veriler başarıyla temizlendi.');
    setTimeout(() => window.location.reload(), 1500);
  };

  const handleReset = async () => {
    const { error } = await resetAllData();
    if (error) {
      setShowResetModal(false);
      showToast('Veri sıfırlama başarısız: ' + error, 'error');
      return;
    }
    triggerRefresh();
    setShowResetModal(false);
    showToast('Tüm veriler başarıyla sıfırlandı.');
    setTimeout(() => window.location.reload(), 1500);
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Kullanıcı';
  const displayEmail = user?.email || '';

  return (
    <div className="space-y-5 max-w-3xl">
      {toastMsg && (
        <div className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-[slideIn_0.3s_ease-out] ${
          toastType === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <i className={toastType === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          <span className="text-sm font-medium">{toastMsg}</span>
        </div>
      )}

      <div>
        <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Ayarlar</h2>
        <p className="text-sm text-foreground-400 mt-0.5">İşletme ayarları ve veri yönetimi</p>
      </div>

      {/* Veri Yönetimi */}
      <SettingsSection title="Veri Yönetimi" icon="ri-database-2-line">
        <p className="text-sm text-foreground-500 mb-5">Verileriniz Supabase sunucusunda güvenli bir şekilde saklanmaktadır. Verilerinizi yedeklemek veya başka bir cihaza taşımak için dışa/içe aktarma özelliklerini kullanabilirsiniz.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={handleExport} className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm cursor-pointer whitespace-nowrap">
            <i className="ri-download-line"></i>
            <span>JSON Olarak Dışa Aktar</span>
          </button>
          <button onClick={handleImport} disabled={importing} className="btn-outline flex items-center gap-2 px-5 py-2.5 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
            <i className={`${importing ? 'ri-loader-4-line animate-spin' : 'ri-upload-line'}`}></i>
            <span>{importing ? 'İçe Aktarılıyor...' : "JSON'dan İçe Aktar"}</span>
          </button>
        </div>

        {/* Import Error */}
        {importError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-500"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-red-700">İçe Aktarma Hatası</p>
                <p className="text-sm text-red-500 mt-0.5">{importError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Import Sonuç Tablosu */}
        {importResult && (
          <div className="mt-4 space-y-4">
            {/* Özet Kartları */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600 font-heading">{importResult.summary.totalImported}</p>
                <p className="text-xs text-emerald-500 mt-0.5">Aktarılan</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 font-heading">{importResult.summary.totalUpdated}</p>
                <p className="text-xs text-amber-500 mt-0.5">Güncellenen</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-500 font-heading">{importResult.summary.totalErrors}</p>
                <p className="text-xs text-red-400 mt-0.5">Hatalı</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-foreground-500 font-heading">{importResult.summary.totalSkipped}</p>
                <p className="text-xs text-foreground-400 mt-0.5">Atlanan</p>
              </div>
            </div>

            {/* Tablo Bazlı Sonuçlar */}
            <div className="overflow-hidden border border-background-200 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground-600">Veri Bölümü</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-foreground-600 w-20">Sonuç</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-foreground-600 w-28">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.tables.filter(t => t.total > 0 || t.errors.length > 0).map((t, i) => {
                    const successCount = t.imported + t.updated;
                    const isFullSuccess = successCount === t.total && t.errors.length === 0;
                    const isPartial = successCount > 0 && (successCount < t.total || t.errors.length > 0);
                    const isFail = successCount === 0 && t.total > 0;
                    return (
                      <tr key={i} className="border-t border-background-100 hover:bg-background-50">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-foreground-700">{t.table === 'customers' ? 'Müşteriler' : t.table === 'products' ? 'Ürünler' : t.table === 'sales' ? 'Satışlar' : t.table === 'sale_items' ? 'Satış Kalemleri' : t.table === 'payments' ? 'Ödemeler' : t.table === 'installments' ? 'Taksitler' : t.table === 'deliveries' ? 'Teslimatlar' : t.table === 'promissory_notes' ? 'Senetler' : t.table === 'enforcement_cases' ? 'İcra Dosyaları' : t.table === 'expenses' ? 'Giderler' : t.table === 'cash_movements' ? 'Kasa Hareketleri' : t.table === 'stock_movements' ? 'Stok Hareketleri' : t.table}</span>
                          {t.errors.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {t.errors.slice(0, 3).map((err, ei) => (
                                <p key={ei} className="text-xs text-red-500 pl-2 border-l-2 border-red-200">{err}</p>
                              ))}
                              {t.errors.length > 3 && <p className="text-xs text-foreground-400 pl-2">+{t.errors.length - 3} hata daha...</p>}
                            </div>
                          )}
                        </td>
                        <td className="text-center px-3 py-2.5">
                          <span className="text-foreground-700 font-medium">{successCount}/{t.total}</span>
                        </td>
                        <td className="text-right px-4 py-2.5">
                          {t.total === 0 ? (
                            <span className="text-xs text-foreground-400">Boş</span>
                          ) : isFullSuccess ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <i className="ri-check-line"></i> Başarılı
                            </span>
                          ) : isPartial ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                              <i className="ri-error-warning-line"></i> Kısmi
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                              <i className="ri-close-line"></i> Başarısız
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Doğrulama Kontrolleri */}
            {importVerifyChecks.length > 0 && (
              <div className="bg-background-100/50 border border-background-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2">
                  <i className="ri-shield-check-line text-emerald-500"></i>
                  İçe Aktarma Sonrası Doğrulama
                </h4>
                <div className="space-y-2">
                  {importVerifyChecks.map((check, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm">
                      {check.passed ? (
                        <i className="ri-checkbox-circle-line text-emerald-500 flex-shrink-0"></i>
                      ) : (
                        <i className="ri-close-circle-line text-red-400 flex-shrink-0"></i>
                      )}
                      <span className="text-foreground-600">{check.label}</span>
                      <span className="text-foreground-400 text-xs ml-auto">{check.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => window.location.reload()}
                className="btn-primary flex items-center gap-2 px-5 py-2 text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                <span>Sayfayı Yenile</span>
              </button>
              <button
                onClick={() => { setImportResult(null); setImportVerifyChecks([]); setImportError(null); }}
                className="btn-outline px-5 py-2 text-sm cursor-pointer whitespace-nowrap"
              >
                Sonuçları Kapat
              </button>
            </div>
          </div>
        )}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="font-semibold text-amber-800 text-sm">Veri Temizleme İşlemleri</h4>
              <p className="text-xs text-amber-600 mt-0.5">Bu işlemler geri alınamaz. Önce yedek almanız önerilir.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowClearDemoModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-amber-300 rounded-lg text-amber-700 bg-background-50 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-delete-back-2-line"></i>
                <span>Demo Verileri Temizle</span>
              </button>
              <button onClick={() => setShowResetModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-red-300 rounded-lg text-red-600 bg-background-50 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-delete-bin-line"></i>
                <span>Tüm Verileri Sıfırla</span>
              </button>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Demo Verileri Temizle Modal */}
      {showClearDemoModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowClearDemoModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-back-2-line text-xl text-amber-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Demo Verileri Temizle</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">Bu işlem localStorage'da bulunan demo verileri temizleyecektir. Eğer kendi eklediğiniz gerçek kayıtlar varsa önce yedek almanız önerilir.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowClearDemoModal(false)} className="flex-1 btn-outline text-center cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleClearDemoData} className="flex-1 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">Temizle</button>
            </div>
          </div>
        </div>
      )}

      {/* Tüm Verileri Sıfırla Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowResetModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Tüm Verileri Sıfırla</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">Bu işlem tüm müşteri, satış, ödeme, stok, teslimat, gider ve kasa kayıtlarını kalıcı olarak silecektir. Emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowResetModal(false)} className="flex-1 btn-outline text-center cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleReset} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Tümünü Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Profili */}
      <SettingsSection title="Kullanıcı Profili" icon="ri-user-settings-line">
        <div className="flex items-center gap-4 mb-5 pb-5 border-b border-background-100">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt={displayName} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <i className="ri-user-3-line text-2xl text-primary-600"></i>
            )}
          </div>
          <div>
            <p className="font-semibold text-foreground-800">{displayName}</p>
            <p className="text-sm text-foreground-400">{displayEmail}</p>
            <input
              ref={avatarFileRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
            <button
              onClick={() => avatarFileRef.current?.click()}
              disabled={avatarUploading}
              className="text-xs text-primary-600 hover:text-primary-700 mt-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {avatarUploading ? 'Yükleniyor...' : 'Fotoğraf Değiştir'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Ad Soyad</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">E-posta</label>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Telefon</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05XX XXX XX XX"
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Rol</label>
            <input
              type="text"
              value={roleLabel}
              readOnly
              className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-500 cursor-not-allowed"
            />
          </div>
        </div>
        <button
          onClick={handleProfileSave}
          disabled={profileSaving || emailSaving}
          className="btn-primary mt-5 px-8 py-2.5 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {profileSaving ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Kaydediliyor...
            </span>
          ) : emailSaving ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              E-posta güncelleniyor...
            </span>
          ) : (
            'Kaydet'
          )}
        </button>
      </SettingsSection>

      {/* İşletme Bilgileri (yalnızca admin) */}
      {role === 'admin' && (
        <SettingsSection title="İşletme Bilgileri" icon="ri-building-4-line">
          {bizLoading ? (
            <p className="text-sm text-foreground-400">Yükleniyor...</p>
          ) : !businessId ? (
            <p className="text-sm text-foreground-400">İşletme bilgisi bulunamadı.</p>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-5 pb-5 border-b border-background-100">
                <div className="w-16 h-16 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {bizLogoUrl ? (
                    <img src={bizLogoUrl} alt={bizName} className="w-16 h-16 object-contain" />
                  ) : (
                    <i className="ri-store-2-line text-2xl text-foreground-300"></i>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-700">İşletme Logosu</p>
                  <input
                    ref={bizLogoFileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBusinessLogoUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3 mt-1">
                    <button
                      onClick={() => bizLogoFileRef.current?.click()}
                      disabled={bizLogoUploading || bizLogoRemoving}
                      className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bizLogoUploading ? 'Yükleniyor...' : bizLogoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}
                    </button>
                    {bizLogoUrl && (
                      <button
                        onClick={handleBusinessLogoRemove}
                        disabled={bizLogoUploading || bizLogoRemoving}
                        className="text-xs text-red-500 hover:text-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bizLogoRemoving ? 'Kaldırılıyor...' : 'Logoyu Kaldır'}
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1">Önerilen ölçü: 512×512px (kare), PNG veya JPG, en fazla 2MB. Sidebar'da gösterilir. Kaldırılırsa yerine işletme adı ve simge otomatik gösterilir.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">İşletme Adı</label>
                  <input type="text" value={bizName} onChange={(e) => setBizName(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">İşletme Türü / Sektör</label>
                  <input type="text" value={bizType} onChange={(e) => setBizType(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Vergi Numarası</label>
                  <input type="text" value={bizTaxNumber} onChange={(e) => setBizTaxNumber(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Vergi Dairesi</label>
                  <input type="text" value={bizTaxOffice} onChange={(e) => setBizTaxOffice(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yetkili Kişi</label>
                  <input type="text" value={bizAuthorizedName} onChange={(e) => setBizAuthorizedName(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yetkili Telefon</label>
                  <input type="text" value={bizAuthorizedPhone} onChange={(e) => setBizAuthorizedPhone(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yetkili E-posta</label>
                  <input type="email" value={bizAuthorizedEmail} onChange={(e) => setBizAuthorizedEmail(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">İşletme Telefonu</label>
                  <input type="text" value={bizPhone} onChange={(e) => setBizPhone(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">İşletme E-postası</label>
                  <input type="email" value={bizEmail} onChange={(e) => setBizEmail(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
                </div>
                <div className="md:col-span-2">
                  <CityDistrictSelect
                    city={bizCity}
                    district={bizDistrict}
                    neighborhood={bizNeighborhood}
                    onCityChange={setBizCity}
                    onDistrictChange={setBizDistrict}
                    onNeighborhoodChange={setBizNeighborhood}
                    selectClassName="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Para Birimi</label>
                  <input type="text" value={bizCurrency} readOnly className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-500 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Durum</label>
                  <input type="text" value={bizStatus} readOnly className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-500 cursor-not-allowed" />
                </div>
              </div>
              <div className="md:col-span-2 mt-4">
                <label className="block text-sm font-medium text-foreground-600 mb-1.5">Açık Adres</label>
                <textarea value={bizAddress} onChange={(e) => setBizAddress(e.target.value)} rows={2} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700" />
              </div>
              <button
                onClick={handleBusinessSave}
                disabled={bizSaving}
                className="btn-primary mt-5 px-8 py-2.5 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bizSaving ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </span>
                ) : (
                  'Kaydet'
                )}
              </button>
            </>
          )}
        </SettingsSection>
      )}

      {/* Şifre Değiştir */}
      <SettingsSection title="Şifre Değiştir" icon="ri-lock-line">
        {passwordError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
            <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0"></i>
            <p className="text-sm text-red-700">{passwordError}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Mevcut Şifre</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
          <div></div>
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yeni Şifre</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="En az 8 karakter"
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-600 mb-1.5">Yeni Şifre (Tekrar)</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
            />
          </div>
        </div>
        <button
          onClick={handlePasswordUpdate}
          disabled={passwordSaving}
          className="btn-primary mt-5 px-8 py-2.5 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {passwordSaving ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Güncelleniyor...
            </span>
          ) : (
            'Şifreyi Güncelle'
          )}
        </button>
      </SettingsSection>

      {/* Döviz Kuru Ayarları */}
      <SettingsSection title="Döviz Kuru Ayarları" icon="ri-exchange-dollar-line">
        <p className="text-sm text-foreground-500 mb-5">Canlı döviz kurlarının Dashboard'da nasıl güncelleneceğini belirleyin. Kurlar public API'ler üzerinden internetten çekilir.</p>
        <ExchangeRateSettingsForm />
      </SettingsSection>

      {/* eLogo Entegrasyonu */}
      <SettingsSection title="eLogo Entegrasyonu" icon="ri-file-text-line">
        <ElogoSettings />
      </SettingsSection>

      {/* Versiyon Bilgisi */}
      <div className="text-center py-6">
        <p className="text-xs text-foreground-300">Polatlar Mobilya Yönetim Paneli v1.0</p>
        <p className="text-[10px] text-foreground-300 mt-0.5">Supabase Tabanlı · Son güncelleme: 28 Haziran 2025</p>
      </div>
    </div>
  );
}